import React, { useId, useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { space } from 'styled-system';

import type { SpaceProps } from '../../theme/system-types';
import type { NextStep } from '../../types/next-steps';
import { BodyBoldBB1 } from '../Typography';
import List from './List';
import ToggleButton from './ToggleButton';

interface NextStepsProps {
  items: Array<Nullable<NextStep>>;
  isCollapsible?: boolean;
  title?: string;
  className?: string;
}

const Wrapper = styled.div<SpaceProps>`
  ${space};
`;

const useCollapsible: () => [boolean, () => void] = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const toggleCollapsed = () => setIsCollapsed(value => !value);

  return [isCollapsed, toggleCollapsed];
};

const NextSteps = ({
  items: nullableItems,
  title,
  isCollapsible = false,
  className,
}: NextStepsProps) => {
  const id = useId();
  const items = nullableItems.filter((item): item is NextStep => item !== null);

  const [isCollapsed, toggleCollapsed] = useCollapsible();
  const isNotCollapsed = !isCollapsible || !isCollapsed;

  return (
    items.length > 0 && (
      <section data-testid="next-steps" className={className}>
        <Wrapper
          {...(isNotCollapsed
            ? {
                mb: ['sp_12', null, null, 'sp_16'],
              }
            : {})}
        >
          {isCollapsible ? (
            <ToggleButton ariaControls={id} isCollapsed={isCollapsed} onClick={toggleCollapsed} />
          ) : null}
          {title ? (
            <BodyBoldBB1 color="sys.neutral.text.strong" mb={['sp_16', null, 'sp_12']}>
              {title}
            </BodyBoldBB1>
          ) : null}
          {isNotCollapsed && <List id={id} items={items} />}
        </Wrapper>
      </section>
    )
  );
};

export default NextSteps;
